import type { Degree, Distance, SpatialRectangle } from "@pdfking/math"
import { Center, Max, Min } from "@pdfking/math"
import { ToPixel } from "@pdfking/quantity"
import { VerticalAlign } from "@pdfking/renderer-api"

import type { Drawable } from "#internal"

import { PRIMARY_COLOR_ID } from "../../../data/ColorList"
import { CreateGroup } from "../../../shapes/CreateGroup"
import { CreateText } from "../../../shapes/CreateText"
import { GrowGuides } from "./GrowGuides"
import { GrowLabel } from "./GrowLabel"

/**
 * Truncate a string to `n` chars with ellipsis. Mirrors python
 * `django.template.defaultfilters.truncatechars`.
 */
const truncatechars = (s: string, n: number): string =>
  s.length <= n ? s : `${s.slice(0, Max(0, n - 1))}…`

/**
 * The name blocks of a grow, one per piece where it landed: what the piece is
 * ({@link GrowLabel}) large, the side — FRONTE or RETRO, the subtitle if the
 * grow has one — under it, the title over it. Every block is turned with its
 * page, and on a collapsed grow's with its piece.
 */
export const DrawGrowName: Drawable = function* (node, _frame, ctx) {
  const grow = node.data?.grow
  if (!grow) return
  for (const { box: landed, page, leaf } of GrowGuides(node, grow)?.cells ?? []) {
    // A piece of a grow laid out as one page among others is called by its
    // own page in the grow, the side that shows — a fold's panels 1 to 6; a
    // page of its own, or a grow that is one piece, by its number in the job.
    // A box's faces by their letters.
    const job_number = ctx.page_number_of(page.guid)
    const number =
      grow.collapsed && grow.leaves.length > 1 && leaf
        ? page.is_front
          ? leaf.meta.page_front
          : leaf.meta.page_rear
        : job_number
    const label = GrowLabel(leaf, number, job_number, grow.grow.is_box)
    // A piece called nothing — a book's blank — carries no block at all.
    if (!label) continue
    const side_text = grow.subtitle ?? (page.is_front ? "FRONTE" : "RETRO")
    const radius = 0.4 * Min(landed.width, landed.height)
    const middle = Center(landed)
    const position: SpatialRectangle<Distance> = {
      x: (middle.x - radius) as Distance,
      y: (middle.y - radius) as Distance,
      width: (2 * radius) as Distance,
      height: (2 * radius) as Distance,
    }
    const texts = [
      CreateText(position, label, {
        text_color: PRIMARY_COLOR_ID,
        font_name: "Helvetica",
        font_size: ToPixel((radius * 0.8) as Distance),
      }),
      CreateText(position, side_text, {
        text_color: PRIMARY_COLOR_ID,
        vertical_align: VerticalAlign.BOTTOM,
        font_name: "Helvetica",
        font_size: ToPixel((radius * 0.3) as Distance),
      }),
      ...(grow.title
        ? [
            CreateText(position, truncatechars(grow.title.toUpperCase(), 12), {
              text_color: PRIMARY_COLOR_ID,
              vertical_align: VerticalAlign.TOP,
              font_name: "Helvetica",
              font_size: ToPixel((radius * 0.3) as Distance),
            }),
          ]
        : []),
    ]
    // A collapsed grow's one page stands upright: each piece on it turns as
    // its own, the calendar's back upside down; a page of its own turns already
    const rotation = (page.rotation + (grow.collapsed ? (leaf?.meta.rotation ?? 0) : 0)) as Degree
    yield* CreateGroup(texts, { origin: middle, rotation })
  }
}
